import React, { useCallback, useState, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { ArrowLeft, Play, HelpCircle, ClipboardList, Undo, Redo, Save, Upload, Plus, X, Zap, Pause, MessageSquare, Star, Tag, SkipForward, RefreshCw, Calendar, Gift, Phone, GitBranch, Clock, Shuffle, CheckCircle, XCircle, PauseCircle, CornerUpLeft, Smartphone, ChevronRight, Layout, ArrowRight, AlertCircle } from 'lucide-react';
interface CancellationFlowPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
// Node type definitions
interface NodeType {
  id: string;
  category: 'trigger' | 'collect' | 'offer' | 'logic' | 'action';
  icon: React.ReactNode;
  name: string;
  description: string;
  color: string;
}
const nodeTypes: NodeType[] = [
// Triggers
{
  id: 'cancel-request',
  category: 'trigger',
  icon: <Zap className="w-4 h-4" data-id="element-7488" data-mp="32s52u6jo1k5r" />,
  name: 'Cancel Request',
  description: 'When customer initiates cancellation',
  color: '#8B5CF6'
}, {
  id: 'pause-request',
  category: 'trigger',
  icon: <Pause className="w-4 h-4" data-id="element-7489" data-mp="17ml3isnjbqxq" />,
  name: 'Pause Request',
  description: 'When customer initiates pause',
  color: '#8B5CF6'
},
// Collect Information
{
  id: 'reason-selection',
  category: 'collect',
  icon: <MessageSquare className="w-4 h-4" data-id="element-7490" data-mp="1go1aeqf55ax7" />,
  name: 'Reason Selection',
  description: "Ask why they're leaving",
  color: '#3B82F6'
}, {
  id: 'feedback-form',
  category: 'collect',
  icon: <MessageSquare className="w-4 h-4" data-id="element-7491" data-mp="oy69dwhx7wsp" />,
  name: 'Feedback Form',
  description: 'Collect written feedback',
  color: '#3B82F6'
}, {
  id: 'survey',
  category: 'collect',
  icon: <Star className="w-4 h-4" data-id="element-7492" data-mp="xr04n3drj6s7" />,
  name: 'Survey',
  description: 'Multiple choice survey',
  color: '#3B82F6'
},
// Retention Offers
{
  id: 'offer-discount',
  category: 'offer',
  icon: <Tag className="w-4 h-4" data-id="element-7493" data-mp="595qaoxq6h99" />,
  name: 'Offer Discount',
  description: 'Offer percentage or fixed discount',
  color: '#10B981'
}, {
  id: 'offer-skip',
  category: 'offer',
  icon: <SkipForward className="w-4 h-4" data-id="element-7494" data-mp="13li1qps781h3" />,
  name: 'Offer Skip',
  description: 'Suggest skipping next order',
  color: '#10B981'
}, {
  id: 'offer-swap',
  category: 'offer',
  icon: <RefreshCw className="w-4 h-4" data-id="element-7495" data-mp="1ich0za759do0" />,
  name: 'Offer Swap',
  description: 'Suggest product swap',
  color: '#10B981'
}, {
  id: 'offer-reschedule',
  category: 'offer',
  icon: <Calendar className="w-4 h-4" data-id="element-7496" data-mp="pm8tqde5cdo7" />,
  name: 'Offer Reschedule',
  description: 'Suggest changing delivery date',
  color: '#10B981'
}, {
  id: 'offer-gift',
  category: 'offer',
  icon: <Gift className="w-4 h-4" data-id="element-7497" data-mp="yngdlqfx1byr" />,
  name: 'Offer Gift',
  description: 'Suggest gifting to someone else',
  color: '#10B981'
}, {
  id: 'offer-pause',
  category: 'offer',
  icon: <Pause className="w-4 h-4" data-id="element-7498" data-mp="2a110yc2azy53" />,
  name: 'Offer Pause',
  description: 'Suggest pausing instead',
  color: '#10B981'
}, {
  id: 'contact-support',
  category: 'offer',
  icon: <Phone className="w-4 h-4" data-id="element-7499" data-mp="262bd1pjn2y77" />,
  name: 'Contact Support',
  description: 'Direct to support team',
  color: '#10B981'
},
// Logic
{
  id: 'condition',
  category: 'logic',
  icon: <GitBranch className="w-4 h-4" data-id="element-7500" data-mp="2ijvzs3hnfpj8" />,
  name: 'Condition',
  description: 'Branch based on reason or response',
  color: '#F59E0B'
}, {
  id: 'delay',
  category: 'logic',
  icon: <Clock className="w-4 h-4" data-id="element-7501" data-mp="28l6es1ughoyr" />,
  name: 'Delay',
  description: 'Wait before next step',
  color: '#F59E0B'
}, {
  id: 'ab-test',
  category: 'logic',
  icon: <Shuffle className="w-4 h-4" data-id="element-7502" data-mp="318sug8gykk1z" />,
  name: 'A/B Test',
  description: 'Split traffic for testing',
  color: '#F59E0B'
},
// Actions
{
  id: 'confirm-cancel',
  category: 'action',
  icon: <XCircle className="w-4 h-4" data-id="element-7503" data-mp="2mibr2jkrjk2g" />,
  name: 'Confirm Cancel',
  description: 'Proceed with cancellation',
  color: '#64748B'
}, {
  id: 'confirm-pause',
  category: 'action',
  icon: <PauseCircle className="w-4 h-4" data-id="element-7504" data-mp="3f7irjo3s2jqc" />,
  name: 'Confirm Pause',
  description: 'Proceed with pause',
  color: '#64748B'
}, {
  id: 'return-portal',
  category: 'action',
  icon: <CornerUpLeft className="w-4 h-4" data-id="element-7505" data-mp="1wc09td4hu97p" />,
  name: 'Return to Portal',
  description: 'Cancel the cancellation flow',
  color: '#64748B'
}];
export function CancellationFlowPage({
  currentPage = 'Settings',
  onNavigate
}: CancellationFlowPageProps) {
  const navigate = useNavigate();
  // State
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [flowStatus, setFlowStatus] = useState<'draft' | 'published'>('draft');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState('Cancellation Flow');
  const [showBanner, setShowBanner] = useState(true);
  // Navigation handler
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Contacts') navigate('/contacts');else if (page === 'Tools') navigate('/management/tools');else if (page === 'Cancellation Flow') navigate('/management/cancellation-flow');else navigate('/');
    }
  };
  // Group nodes by category
  const groupedNodes = {
    trigger: nodeTypes.filter(n => n.category === 'trigger'),
    collect: nodeTypes.filter(n => n.category === 'collect'),
    offer: nodeTypes.filter(n => n.category === 'offer'),
    logic: nodeTypes.filter(n => n.category === 'logic'),
    action: nodeTypes.filter(n => n.category === 'action')
  };
  const handleSaveDraft = () => {
    setHasUnsavedChanges(false);
    // Save logic here
  };
  const handlePublish = () => {
    setFlowStatus('published');
    setHasUnsavedChanges(false);
    // Publish logic here
  };
  // Check if mobile/tablet
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  if (isMobile) {
    return <div className="flex h-screen bg-[#F1F5F9] items-center justify-center p-6" data-id="element-7506" data-mp="3i9n9xtt74jvo">
        <div className="max-w-md text-center" data-id="element-7507" data-mp="3dblmkwoqlyee">
          <Layout className="w-16 h-16 text-[#CBD5E1] mx-auto mb-4" data-id="element-7508" data-mp="3dcwqcwi6n65i" />
          <h2 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-7509" data-mp="3s0hyx5xttxkt">
            Flow Builder Requires a Larger Screen
          </h2>
          <p className="text-[#64748B]" data-id="element-7510" data-mp="3n88bbwk6p9lp">
            The visual flow builder is optimized for desktop screens (1024px and
            wider). Please use a desktop or laptop computer to access this
            feature.
          </p>
        </div>
      </div>;
  }
  return <div className="flex flex-col overflow-hidden h-screen" data-id="element-7511" data-mp="etzl2v54wuwp">
      {/* Row 1: Page Header */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-4" data-id="element-7512" data-mp="9udbqwsffyt6">
        <div className="flex items-center justify-between" data-id="element-7513" data-mp="2gl31y25mb5pp">
          <div className="flex items-center gap-4" data-id="element-7514" data-mp="2glmfl6o8j7xx">
            <button onClick={() => navigate('/management/tools')} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-7515" data-mp="2bmjbbog2x5r0">
              <ArrowLeft className="w-5 h-5 text-[#64748B]" data-id="element-7516" data-mp="r5a3m1fii47k" />
            </button>
            <h1 className="text-[24px] font-semibold text-[#0F172A]" data-id="element-7517" data-mp="w3bqyyjz0pou">
              Cancellation Flow
            </h1>
          </div>
          <button className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" title="Help Documentation" data-id="element-7518" data-mp="hdvzthgj2i8m">
            <HelpCircle className="w-5 h-5 text-[#64748B]" data-id="element-7519" data-mp="mb5vheuifq4w" />
          </button>
        </div>
      </div>

      {/* Row 2: Roadmap Banner */}
      {showBanner && <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-6 py-3" data-id="element-7520" data-mp="cg48aojsoy4y">
          <div className="flex items-center justify-between gap-4" data-id="element-7521" data-mp="3ukxt92bedx7l">
            <div className="flex items-center gap-3" data-id="element-7522" data-mp="3ums9i3bq515c">
              <span className="inline-flex items-center px-2 py-1 rounded text-[11px] font-semibold uppercase tracking-wide bg-white border border-[#8B5CF6] text-[#8B5CF6]" data-id="element-7523" data-mp="3ffsboaucnpf">
                COMING Q2 2026
              </span>
              <span className="text-[14px] text-[#64748B]" data-id="element-7524" data-mp="1xrmhxs4hf719">
                Visual flow builder coming soon
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-7525" data-mp="gk7lndknur1f">
              <button onClick={() => navigate('/management/tools/cancellation-settings')} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] flex items-center gap-1 transition-colors" data-id="element-7526" data-mp="gkr6b21p3tj5">
                Use Current Version
                <ArrowRight className="w-4 h-4" data-id="element-7527" data-mp="1utgyis14xsd" />
              </button>
              <button onClick={() => setShowBanner(false)} className="p-1 hover:bg-[#E2E8F0] rounded transition-colors" data-id="element-7528" data-mp="1sp0709184ao">
                <X className="w-4 h-4 text-[#64748B]" data-id="element-7529" data-mp="3ozhm6h8wjo73" />
              </button>
            </div>
          </div>
        </div>}

      {/* Row 3: Canvas Toolbar */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-3" data-id="element-7530" data-mp="3k2iue5cpntc2">
        <div className="flex items-center justify-between" data-id="element-7531" data-mp="1n7d0hnhzxn4t">
          <div className="flex items-center gap-4" data-id="element-7532" data-mp="1i9beatp9x7p4">
            <select value={selectedFlow} onChange={e => setSelectedFlow(e.target.value)} className="h-[36px] px-3 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundPosition: 'right 8px center',
            backgroundSize: '14px 14px'
          }} data-id="element-7533" data-mp="1i9un9kwy4lr0">
              <option data-id="element-7534" data-mp="1gu8npub0ww92">Cancellation Flow</option>
              <option data-id="element-7535" data-mp="1ktzn2mem8kp7">Pause Flow</option>
              <option value="new" data-id="element-7536" data-mp="1kv2c0osdod2h">+ New Flow</option>
            </select>
            <Badge variant="warning" data-id="element-7537" data-mp="2s6j9hgrcns40">Draft</Badge>
          </div>
          <div className="flex items-center gap-3" data-id="element-7538" data-mp="2w8mrko8ixuy9">
            <div className="relative group" data-id="element-7539" data-mp="2w7386orkn3s9">
              <button className="p-2 rounded-md transition-colors opacity-40 cursor-not-allowed" title="Undo" disabled data-id="element-7540" data-mp="dgodwaoph09a">
                <Undo className="w-4 h-4 text-[#64748B]" data-id="element-7541" data-mp="ie6dvbkqw555" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7542" data-mp="n5eogaxt9wjw">
                Available when flow builder launches
              </div>
            </div>
            <div className="relative group" data-id="element-7543" data-mp="3vmjaov55epup">
              <button className="p-2 rounded-md transition-colors opacity-40 cursor-not-allowed" title="Redo" disabled data-id="element-7544" data-mp="4fggrvxxe5gs">
                <Redo className="w-4 h-4 text-[#64748B]" data-id="element-7545" data-mp="9f2wcc187xsr" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7546" data-mp="1fuvo9tnrgfq5">
                Available when flow builder launches
              </div>
            </div>
            <div className="w-px h-6 bg-[#E2E8F0]" data-id="element-7547" data-mp="30dpf8a56zds5" />
            <div className="relative group" data-id="element-7548" data-mp="30f0jeb2junjs">
              <Button variant="secondary" size="sm" iconLeft={<Save className="w-4 h-4" data-id="element-7550" data-mp="2vjl6pmga2xoy" />} disabled className="opacity-40 cursor-not-allowed" data-id="element-7549" data-mp="2vkwavndmy7gl">
                Save Draft
              </Button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7551" data-mp="2lot7ke71s2s2">
                Available when flow builder launches
              </div>
            </div>
            <div className="relative group" data-id="element-7552" data-mp="27ptij5fmhlvd">
              <Button variant="primary" size="sm" iconLeft={<Upload className="w-4 h-4" data-id="element-7554" data-mp="av6xleskytq0" />} disabled className="opacity-40 cursor-not-allowed" data-id="element-7553" data-mp="aw8jkeb4x0mi">
                Publish
              </Button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-7555" data-mp="20d4100sbn0a0">
                Available when flow builder launches
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content: Three-panel layout */}
      <div className="flex-1 flex overflow-hidden" data-id="element-7556" data-mp="2j32a8k7uuzf8">
        {/* Left Sidebar: Node Palette */}
        <div className="w-[280px] bg-white border-r border-[#E2E8F0] overflow-y-auto" data-id="element-7557" data-mp="2o2oomwzf6lpm">
          <div className="p-4" data-id="element-7558" data-mp="295nt0ie3y6lv">
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-4" data-id="element-7559" data-mp="294cqelwef4wc">
              Flow Elements
            </h3>

            {/* Triggers */}
            <div className="mb-5" data-id="element-7560" data-mp="xpz95zw5hnpa">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-7561" data-mp="xoxt1h0a2b0p">
                TRIGGERS
              </h4>
              <div className="space-y-2" data-id="element-7562" data-mp="12mplqd8qy15y">
                {groupedNodes.trigger.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#8B5CF6] hover:shadow-sm transition-all group" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-7563" data-mp="3gv08iiipm29c">
                    <div className="flex items-center gap-2" data-id="element-7564" data-mp="3lwfss8d2c4qo">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-7565" data-mp="2y5tv9b0oq2ux">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7566" data-mp="2eicq3neoama3">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-7567" data-mp="2egta7xwxid8u">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-7568" data-mp="2n6ie79iyxi8a">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Collect Information */}
            <div className="mb-5" data-id="element-7569" data-mp="31velv5h44t8d">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-7570" data-mp="31wh9n4j52fk2">
                COLLECT INFORMATION
              </h4>
              <div className="space-y-2" data-id="element-7571" data-mp="3av1m3ny3vszu">
                {groupedNodes.collect.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#3B82F6] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-7572" data-mp="3axfcvklr6j1l">
                    <div className="flex items-center gap-2" data-id="element-7573" data-mp="1lir6bzzufvbh">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-7574" data-mp="1qgiuqk1a4zax">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7575" data-mp="147u6o96iolmj">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-7576" data-mp="1457qxm9h2zt8">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-7577" data-mp="1wx15l99vvd8b">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Retention Offers */}
            <div className="mb-5" data-id="element-7578" data-mp="gisss6ex0i9v">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-7579" data-mp="gfn42sa77ieo">
                RETENTION OFFERS
              </h4>
              <div className="space-y-2" data-id="element-7580" data-mp="2zobzohtl6xh5">
                {groupedNodes.offer.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#10B981] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-7581" data-mp="33px9fuatqvn4">
                    <div className="flex items-center gap-2" data-id="element-7582" data-mp="2g0mhn2bgcbl4">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-7583" data-mp="2fz1ptkgiv6qy">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7584" data-mp="1x2s01zw9crr4">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-7585" data-mp="1x436y8l02fmg">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-7586" data-mp="1ifteumnzutku">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Logic */}
            <div className="mb-5" data-id="element-7587" data-mp="1ndd1a9uuicnv">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-7588" data-mp="1ng7mw4pli3iv">
                LOGIC
              </h4>
              <div className="space-y-2" data-id="element-7589" data-mp="1xd3wkdr8cvp0">
                {groupedNodes.logic.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#F59E0B] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-7590" data-mp="2b3pfg2gop5mw">
                    <div className="flex items-center gap-2" data-id="element-7591" data-mp="2kziq1gauep21">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-7592" data-mp="2g43if63fhpe2">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7593" data-mp="1fo0aij7yzbx9">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-7594" data-mp="1fle2kih62uei">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-7595" data-mp="mzunfptg1au9">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-[#E2E8F0] pt-5" data-id="element-7596" data-mp="3vdtjergf0w7r">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-7597" data-mp="48l9n4cn3zwv">
                ACTIONS
              </h4>
              <div className="space-y-2" data-id="element-7598" data-mp="2hhjtgp1lnxc4">
                {groupedNodes.action.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#64748B] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-7599" data-mp="2dgzv5i5ainoe">
                    <div className="flex items-center gap-2" data-id="element-7600" data-mp="1yjqrpq2pptgb">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-7601" data-mp="1ym3eqhnbuu08">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-7602" data-mp="1fs6bzonohfkb">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-7603" data-mp="1auobmmnq88nx">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94748B] truncate" data-id="element-7604" data-mp="19la0j32xbioh">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Center: Canvas with Static Demo Flow */}
        <div className="flex-1 relative bg-[#FAFBFC] overflow-auto" data-id="element-7605" data-mp="qnpcfy7zgo4x">
          {/* Static Demo Flow */}
          <div className="absolute inset-0 p-12" data-id="element-7606" data-mp="lvyzrllkpoac">
            <svg width="100%" height="100%" className="absolute inset-0" style={{
            zIndex: 0
          }} data-id="element-7607" data-mp="lue1pm01vn7q">
              {/* Connection lines */}
              {/* Start to Reason */}
              <path d="M 400 120 L 400 200" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-7608" data-mp="c048k23aakvy" />

              {/* Reason to branches */}
              <path d="M 400 280 Q 250 350 150 420" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="5,5" markerEnd="url(#arrowhead)" data-id="element-7609" data-mp="2xcxi3eblr4u" />
              <path d="M 400 280 L 400 420" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="5,5" markerEnd="url(#arrowhead)" data-id="element-7610" data-mp="2en2npetob1ev" />
              <path d="M 400 280 Q 550 350 650 420" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="5,5" markerEnd="url(#arrowhead)" data-id="element-7611" data-mp="2nlejdv9ecpe6" />

              {/* Branches to confirmation */}
              <path d="M 150 520 Q 250 590 400 620" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-7612" data-mp="1vv09090ldv79" />
              <path d="M 400 520 L 400 620" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-7613" data-mp="1zwvcnk5zwko7" />
              <path d="M 650 520 Q 550 590 400 620" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-7614" data-mp="24tu4fw26sfj8" />

              {/* Confirmation to outcomes */}
              <path d="M 400 700 Q 300 750 250 800" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-7615" data-mp="1c5pv90kepkbs" />
              <path d="M 400 700 Q 500 750 550 800" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-7616" data-mp="1h3hmrth534fg" />

              <defs data-id="element-7617" data-mp="2fhn6tf5sdixx">
                <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" data-id="element-7618" data-mp="2fi6qb0b34fe2">
                  <polygon points="0 0, 10 3, 0 6" fill="#CBD5E1" data-id="element-7619" data-mp="2t7qm1m690cdv" />
                </marker>
              </defs>
            </svg>

            {/* Flow Nodes */}
            <div className="relative" style={{
            zIndex: 1
          }} data-id="element-7620" data-mp="21hm7w5930ll3">
              {/* START: Cancel Request */}
              <div className="absolute" style={{
              left: '280px',
              top: '40px'
            }} data-id="element-7621" data-mp="1wjawvr71w51a">
                <div className="w-[240px] bg-white border-l-4 border-[#8B5CF6] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7622" data-mp="2aj3i56zt5t1v">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7623" data-mp="3lx7ay9untb5c">
                    <div className="p-2 rounded bg-[#8B5CF615]" data-id="element-7624" data-mp="3lvmkavbguccr">
                      <Zap className="w-5 h-5 text-[#8B5CF6]" data-id="element-7625" data-mp="3gxcn6f66l1oi" />
                    </div>
                    <div className="flex-1" data-id="element-7626" data-mp="3pf52ijqj9swr">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7627" data-mp="3phhjouogvz8r">
                        Cancel Request
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7628" data-mp="3fi0gql0lnzgh">
                        When customer initiates cancellation
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* NODE 2: Reason Selection */}
              <div className="absolute" style={{
              left: '280px',
              top: '180px'
            }} data-id="element-7629" data-mp="h036ajkcmsqo">
                <div className="w-[240px] bg-white border-l-4 border-[#3B82F6] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7630" data-mp="29niqy4gn8pc">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7631" data-mp="uzcqduboe6zx">
                    <div className="p-2 rounded bg-[#3B82F615]" data-id="element-7632" data-mp="uy1k9nurcn5n">
                      <MessageSquare className="w-5 h-5 text-[#3B82F6]" data-id="element-7633" data-mp="zqco31smct3h" />
                    </div>
                    <div className="flex-1" data-id="element-7634" data-mp="379w8a5d66hnp">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7635" data-mp="377jk3agtjb27">
                        Reason Selection
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7636" data-mp="2y86amhsp2mhh">
                        Why are you leaving?
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-[#94A3B8] space-y-1" data-id="element-7637" data-mp="3qy518hmjero9">
                    <div data-id="element-7638" data-mp="3lyk0c1nt498w">• Too expensive</div>
                    <div data-id="element-7639" data-mp="3lzv6g84q5t36">• Don't need it</div>
                    <div data-id="element-7640" data-mp="3h7ty2xaogmif">• Other reasons</div>
                  </div>
                </div>
              </div>

              {/* Branch labels */}
              <div className="absolute" style={{
              left: '180px',
              top: '330px'
            }} data-id="element-7641" data-mp="3c7pid9hlfvt9">
                <span className="inline-block px-2 py-1 bg-white border border-[#E2E8F0] rounded text-[11px] text-[#64748B] shadow-sm" data-id="element-7642" data-mp="dk1p5r2kkdl2">
                  Too expensive
                </span>
              </div>
              <div className="absolute" style={{
              left: '370px',
              top: '330px'
            }} data-id="element-7643" data-mp="g59h13tasl0r">
                <span className="inline-block px-2 py-1 bg-white border border-[#E2E8F0] rounded text-[11px] text-[#64748B] shadow-sm" data-id="element-7644" data-mp="q1cis8k8mhn6">
                  Don't need it
                </span>
              </div>
              <div className="absolute" style={{
              left: '600px',
              top: '330px'
            }} data-id="element-7645" data-mp="z2almnixwj46">
                <span className="inline-block px-2 py-1 bg-white border border-[#E2E8F0] rounded text-[11px] text-[#64748B] shadow-sm" data-id="element-7646" data-mp="140c8zknef4lg">
                  Other
                </span>
              </div>

              {/* BRANCH A: Offer Discount */}
              <div className="absolute" style={{
              left: '30px',
              top: '400px'
            }} data-id="element-7647" data-mp="1dt048ayqd0i2">
                <div className="w-[240px] bg-white border-l-4 border-[#10B981] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7648" data-mp="1mrc1gvu3qwjh">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7649" data-mp="3jsrek9ulvxrh">
                    <div className="p-2 rounded bg-[#10B98115]" data-id="element-7650" data-mp="3etxxk7g41omk">
                      <Tag className="w-5 h-5 text-[#10B981]" data-id="element-7651" data-mp="3skjgtx9h80kz" />
                    </div>
                    <div className="flex-1" data-id="element-7652" data-mp="3sme4vkg1o8ta">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7653" data-mp="3ntl23244zaju">
                        Offer Discount
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7654" data-mp="6ffs8dhk0n6n">
                        20% off next 3 months
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* BRANCH B: Offer Pause */}
              <div className="absolute" style={{
              left: '280px',
              top: '400px'
            }} data-id="element-7655" data-mp="bcxvpocsya79">
                <div className="w-[240px] bg-white border-l-4 border-[#F59E0B] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7656" data-mp="2h7h3db2obr51">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7657" data-mp="230x8jj3bgnb7">
                    <div className="p-2 rounded bg-[#F59E0B15]" data-id="element-7658" data-mp="2717q7t0h125w">
                      <Pause className="w-5 h-5 text-[#F59E0B]" data-id="element-7659" data-mp="270o563fixxnn" />
                    </div>
                    <div className="flex-1" data-id="element-7660" data-mp="1t9jcjlakq5mt">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7661" data-mp="1y8w1qcox3osk">
                        Offer Pause
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7662" data-mp="1jabr0ator4om">
                        Pause for up to 3 months
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* BRANCH C: Feedback Form */}
              <div className="absolute" style={{
              left: '530px',
              top: '400px'
            }} data-id="element-7663" data-mp="1fbbbp2af2k6d">
                <div className="w-[240px] bg-white border-l-4 border-[#3B82F6] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7664" data-mp="34pr832rl8goz">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7665" data-mp="m3voz1malglh">
                    <div className="p-2 rounded bg-[#3B82F615]" data-id="element-7666" data-mp="m5gluxw2xbmi">
                      <MessageSquare className="w-5 h-5 text-[#3B82F6]" data-id="element-7667" data-mp="h5kdkmgcmy15" />
                    </div>
                    <div className="flex-1" data-id="element-7668" data-mp="cc80n9l18fo8">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7669" data-mp="8bxmvar00kyt">
                        Feedback Form
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7670" data-mp="3dnq4vpmlcb3">
                        Help us improve
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* NODE 4: Final Confirmation */}
              <div className="absolute" style={{
              left: '280px',
              top: '600px'
            }} data-id="element-7671" data-mp="3lizb8h70h9qx">
                <div className="w-[240px] bg-white border-l-4 border-[#64748B] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7672" data-mp="1nro9k5qfg3c9">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7673" data-mp="31oqz8p5sargj">
                    <div className="p-2 rounded bg-[#64748B15]" data-id="element-7674" data-mp="31oh9n2onimbd">
                      <AlertCircle className="w-5 h-5 text-[#64748B]" data-id="element-7675" data-mp="36n0zvfaa2k9j" />
                    </div>
                    <div className="flex-1" data-id="element-7676" data-mp="3beakg1gyyxek">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7677" data-mp="3bgna70syyc26">
                        Final Confirmation
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7678" data-mp="2iqn5p32dr0zu">
                        Are you sure?
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* END NODE A: Cancelled */}
              <div className="absolute" style={{
              left: '130px',
              top: '780px'
            }} data-id="element-7679" data-mp="2wpmty9lzddvh">
                <div className="w-[240px] bg-white border-l-4 border-[#EF4444] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7680" data-mp="70u9a9xh3htr">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7681" data-mp="c1i83hry12yj">
                    <div className="p-2 rounded bg-[#EF444415]" data-id="element-7682" data-mp="132jgtpbw6cws">
                      <XCircle className="w-5 h-5 text-[#EF4444]" data-id="element-7683" data-mp="1801prfpwss4s" />
                    </div>
                    <div className="flex-1" data-id="element-7684" data-mp="tbotehmwt79r">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7685" data-mp="tam0y59xuwrq">
                        Cancelled
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7686" data-mp="kaqnftswupoj">
                        Subscription ended
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* END NODE B: Retained */}
              <div className="absolute" style={{
              left: '430px',
              top: '780px'
            }} data-id="element-7687" data-mp="aedu4x1a2dub">
                <div className="w-[240px] bg-white border-l-4 border-[#10B981] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-7688" data-mp="fdh0ckszuq3v">
                  <div className="flex items-center gap-3 mb-2" data-id="element-7689" data-mp="29ocbnuvkcjac">
                    <div className="p-2 rounded bg-[#10B98115]" data-id="element-7690" data-mp="3u76b6zr28pny">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" data-id="element-7691" data-mp="3u5ldj19g8qlv" />
                    </div>
                    <div className="flex-1" data-id="element-7692" data-mp="3p6icdrwlb4j6">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7693" data-mp="7sd5nc5b0xa7">
                        Retained
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-7694" data-mp="303l6bmykpfb">
                        Customer saved
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Zoom controls */}
          <div className="absolute bottom-6 left-6 flex flex-col gap-2 bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-7695" data-mp="qqh49cnkfdxr">
            <button className="p-2 hover:bg-[#F1F5F9] transition-colors" data-id="element-7696" data-mp="2sic7r4zo4c4w">
              <Plus className="w-4 h-4 text-[#64748B]" data-id="element-7697" data-mp="2skgdztl1hn8c" />
            </button>
            <div className="h-px bg-[#E2E8F0]" data-id="element-7698" data-mp="2nktz7fpkbyxf" />
            <button className="p-2 hover:bg-[#F1F5F9] transition-colors" data-id="element-7699" data-mp="3d7oakcacg5jk">
              <X className="w-4 h-4 text-[#64748B]" data-id="element-7700" data-mp="3d9hhlsovcrs3" />
            </button>
          </div>

          {/* Minimap */}
          <div className="absolute bottom-6 right-6 w-48 h-32 bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-2" data-id="element-7701" data-mp="3m82zrmqfi4sc">
            <div className="w-full h-full bg-[#F8FAFC] rounded relative overflow-hidden" data-id="element-7702" data-mp="3r7fx51exeu96">
              {/* Miniature flow visualization */}
              <div className="absolute inset-2" data-id="element-7703" data-mp="mlf3mibcqe">
                <div className="w-full h-full flex flex-col items-center justify-center gap-1" data-id="element-7704" data-mp="41ycldl1460p">
                  <div className="w-12 h-2 bg-[#8B5CF6] rounded-full opacity-50" data-id="element-7705" data-mp="8sojiv93sgxi"></div>
                  <div className="w-12 h-2 bg-[#3B82F6] rounded-full opacity-50" data-id="element-7706" data-mp="ds030c0uu0is"></div>
                  <div className="flex gap-1" data-id="element-7707" data-mp="du47ow6kv3k4">
                    <div className="w-5 h-2 bg-[#10B981] rounded-full opacity-50" data-id="element-7708" data-mp="isfe0wxnyvxl"></div>
                    <div className="w-5 h-2 bg-[#F59E0B] rounded-full opacity-50" data-id="element-7709" data-mp="npo0vatrbo56"></div>
                    <div className="w-5 h-2 bg-[#3B82F6] rounded-full opacity-50" data-id="element-7710" data-mp="28917t2ha30ik"></div>
                  </div>
                  <div className="w-12 h-2 bg-[#64748B] rounded-full opacity-50" data-id="element-7711" data-mp="2395blpetoddk"></div>
                  <div className="flex gap-1" data-id="element-7712" data-mp="1y9shcjlmg40y">
                    <div className="w-5 h-2 bg-[#EF4444] rounded-full opacity-50" data-id="element-7713" data-mp="1yakbdpzkltp4"></div>
                    <div className="w-5 h-2 bg-[#10B981] rounded-full opacity-50" data-id="element-7714" data-mp="1tcs9c357or77"></div>
                  </div>
                </div>
                {/* Viewport indicator */}
                <div className="absolute inset-0 border-2 border-[#0EA5E9] rounded opacity-30" data-id="element-7715" data-mp="2lzmul29urulq"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}